import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import {
  GetProviderName,
  type GPUInstanceType,
} from "types/InstanceTypes";
import type { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import { displayPlatformType } from "components/Environment/utils";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { UserContext } from "contexts/UserContext";
import type {
  LaunchableParameter,
  LaunchableParameterBinding,
  NGCAPIKeySpec,
} from "server";
import type { ManagedSecretSummary } from "server/devplane/managedSecrets/api";
import { useManagedSecretsQuery } from "server/devplane/managedSecrets/queries";
import { getLaunchableManagedSecretsAvailableToUser } from "components/Launchables/shared/managedSecrets";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
import { getDefaultStorageGiB } from "utils/instanceTypes";
import { hasInstanceSelectionChanged } from "../configuration/compute/instanceSelection";
import type { NgcApiKeyDeployResult } from "../configuration/ngc-api-key/NgcApiKeyOption";
import { useDeployConfiguration } from "../hooks/useDeployConfiguration";
import { useDeployPrice } from "../hooks/useDeployPrice";
import { useDeployRequest } from "../hooks/useDeployRequest";
import { launchableParameterSupportsManagedSecret } from "../configuration/parameters/LaunchableParameters";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";

export interface LaunchableDeploymentProviderProps {
  children: React.ReactNode;
  instanceType: GPUInstanceType | null;
  defaultInstanceType: GPUInstanceType | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceStorage: string | null;
  instanceLoading: boolean;
  intendedCloudCredentialId: string | null;
  intendedFirewallRules: CreateFirewallRule[] | null;
  imageId?: string | null;
  loading: boolean;
  isPublic?: boolean;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
  parameters?: LaunchableParameter[];
  nameErrorMessage: string;
  allInstances: GPUInstanceType[];
  onDeploy: (
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    location?: string | null,
  ) => Promise<NgcApiKeyDeployResult | void> | NgcApiKeyDeployResult | void;
  onInstanceChange?: (
    instanceType: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  deployLabel?: string;
  priceDisclaimer?: string;
  priceLabel?: string;
  instanceName?: string;
  onInstanceNameChange?: (name: string) => void;
  initialLocation?: string | null;
}

type ConfigurationState = ReturnType<typeof useDeployConfiguration>;
type DeployRequestState = ReturnType<typeof useDeployRequest>;
type DeployPrice = ReturnType<typeof useDeployPrice>;

export interface LaunchableDeploymentContextValue
  extends ConfigurationState, DeployRequestState, DeployPrice {
  instanceType: GPUInstanceType | null;
  defaultInstanceType: GPUInstanceType | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceStorage: string | null;
  instanceLoading: boolean;
  intendedCloudCredentialId: string | null;
  intendedFirewallRules: CreateFirewallRule[] | null;
  imageId?: string | null;
  loading: boolean;
  isPublic?: boolean;
  nameErrorMessage: string;
  allInstances: GPUInstanceType[];
  onInstanceChange?: (
    instanceType: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  deployLabel?: string;
  instanceName?: string;
  onInstanceNameChange?: (name: string) => void;
  initLoading: boolean;
  parameters: LaunchableParameter[];
  providerName: string | null;
  shouldCollectParameters: boolean;
  selectedInstanceIsOutOfStock: boolean;
  availableSecrets: ManagedSecretSummary[];
  secretsLoading: boolean;
  organizationId: string;
  managedSecretsEnabled: boolean;
}

export const LaunchableDeploymentContext =
  createContext<LaunchableDeploymentContextValue | null>(null);

export const LaunchableDeploymentProvider: React.FC<
  LaunchableDeploymentProviderProps
> = ({
  children,
  instanceType,
  defaultInstanceType,
  intendedInstanceType,
  intendedDiskStorage,
  instanceStorage,
  instanceLoading,
  intendedCloudCredentialId,
  intendedFirewallRules,
  imageId,
  loading,
  isPublic,
  ngcApiKeySpec,
  parameters = [],
  nameErrorMessage,
  allInstances,
  onDeploy,
  onInstanceChange,
  deployLabel,
  priceDisclaimer,
  priceLabel,
  instanceName,
  onInstanceNameChange,
  initialLocation,
}) => {
  const cloudCredential = getPrimaryInstanceCloudCred(instanceType);
  const { initLoading } = useContext(OneClickDeployContext);
  const { isOrgAccessLoading, activeOrgId, activeOrgRole } =
    useContext(OrgContext);
  const { isInstancesLoading } = useContext(InstanceTypesContext);
  const { me, isLoading: isUserLoading } = useContext(UserContext);
  const shouldCollectParameters = !isPublic;
  const managedSecretsEnabled =
    useIdentityFeatureFlagEnabled(POSTHOG_IDENTITY_FLAGS.BREV_SECRETS_UI) ===
    true;
  const managedSecretsQuery = useManagedSecretsQuery(
    activeOrgId,
    managedSecretsEnabled &&
      shouldCollectParameters &&
      parameters.some(launchableParameterSupportsManagedSecret),
  );
  const availableSecrets = managedSecretsEnabled
    ? getLaunchableManagedSecretsAvailableToUser(
        managedSecretsQuery.data ?? [],
        me?.id,
        activeOrgRole,
      )
    : [];
  const selectedInstanceIsOutOfStock =
    !isPublic &&
    !isInstancesLoading &&
    !!instanceType &&
    !allInstances.some(
      (availableInstance) =>
        availableInstance.type === instanceType.type &&
        getPrimaryInstanceCloudCred(availableInstance)?.id ===
          cloudCredential?.id,
    );

  const configuration = useDeployConfiguration({
    initialLocation,
    parameters,
    collectParameters: shouldCollectParameters,
    allowManagedSecrets: managedSecretsEnabled,
  });
  const effectiveParameterSecretSelections = managedSecretsEnabled
    ? configuration.parameterSecretSelections
    : {};
  const { resetForInstanceChange } = configuration;
  const handleInstanceChange = useCallback(
    (nextInstanceType: GPUInstanceType | null, diskStorage: string) => {
      if (hasInstanceSelectionChanged(instanceType, nextInstanceType)) {
        resetForInstanceChange();
      }
      onInstanceChange?.(nextInstanceType, diskStorage);
    },
    [instanceType, onInstanceChange, resetForInstanceChange],
  );

  useEffect(() => {
    if (
      isPublic ||
      !onInstanceChange ||
      (instanceType && cloudCredential?.platformType !== "custom")
    ) {
      return;
    }

    const intendedInstance = allInstances.find(
      (instance) =>
        instance.type === intendedInstanceType ||
        instance.type === intendedInstanceType?.split("@")[1],
    );
    if (intendedInstance) {
      if (!hasInstanceSelectionChanged(instanceType, intendedInstance)) {
        return;
      }
      handleInstanceChange(
        intendedInstance,
        intendedDiskStorage ||
          getDefaultStorageGiB(intendedInstance.supported_storage),
      );
    } else if (instanceType !== null) {
      handleInstanceChange(null, intendedDiskStorage || "120");
    }
  }, [
    allInstances,
    cloudCredential?.platformType,
    isPublic,
    instanceType,
    intendedDiskStorage,
    intendedInstanceType,
    handleInstanceChange,
    onInstanceChange,
  ]);

  const request = useDeployRequest({
    isPublic,
    loading,
    initLoading,
    computeHasErrors:
      !isPublic &&
      (!instanceType ||
        !configuration.storageValid ||
        configuration.isInstanceOutOfStock ||
        selectedInstanceIsOutOfStock),
    ngcApiKeySpec,
    parameters,
    parameterValues: configuration.parameterValues,
    parameterSecretSelections: effectiveParameterSecretSelections,
    selectedLocation: configuration.selectedLocation,
    missingRequiredParameters: configuration.missingRequiredParameters,
    onDeploy,
  });
  const price = useDeployPrice({
    instanceType,
    instanceStorage,
    intendedDiskStorage,
    priceDisclaimer,
    priceLabel,
    location: configuration.selectedLocation,
  });
  const providerName = instanceType
    ? displayPlatformType(GetProviderName(instanceType) || "Unknown")
    : null;

  return (
    <LaunchableDeploymentContext.Provider
      value={{
        instanceType,
        defaultInstanceType,
        intendedInstanceType,
        intendedDiskStorage,
        instanceStorage,
        instanceLoading,
        intendedCloudCredentialId,
        intendedFirewallRules,
        imageId,
        loading,
        isPublic,
        parameters,
        nameErrorMessage,
        allInstances,
        onInstanceChange: handleInstanceChange,
        deployLabel,
        instanceName,
        onInstanceNameChange,
        initLoading,
        providerName,
        shouldCollectParameters,
        selectedInstanceIsOutOfStock,
        availableSecrets,
        secretsLoading:
          managedSecretsEnabled &&
          (managedSecretsQuery.isLoading ||
            isUserLoading ||
            isOrgAccessLoading),
        organizationId: activeOrgId,
        managedSecretsEnabled,
        ...configuration,
        parameterSecretSelections: effectiveParameterSecretSelections,
        ...request,
        ...price,
      }}
    >
      {children}
    </LaunchableDeploymentContext.Provider>
  );
};
